import PropTypes from "prop-types"

/**
 * Shape of the display tariff shared by ConnectorSelector and TariffSummary,
 * built by apps/backend/src/helpers/tariff-display.ts.
 */
const displayTariffRulePropTypes = PropTypes.shape({
	start_time: PropTypes.string,
	end_time: PropTypes.string,
	price_per_kwh: PropTypes.number,
	price_per_minute: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
	minimum_fee: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
	activation_fee: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
	vat_breakdown: PropTypes.arrayOf(
		PropTypes.shape({
			component: PropTypes.string,
			vat: PropTypes.number,
		})
	),
})

export const displayTariffPropTypes = PropTypes.shape({
	scope: PropTypes.oneOf(["tenant_default", "charging_station", "none"]),
	currency: PropTypes.string,
	rule: displayTariffRulePropTypes,
})

/**
 * Shape of the active CEME price list sent in price list mode, built by
 * buildDisplayPriceList in apps/backend/src/helpers/tariff-display.ts.
 */
export const displayPriceListPropTypes = PropTypes.shape({
	currency: PropTypes.string,
	energy_price_per_kwh: PropTypes.number,
	charging_fee: PropTypes.number,
	egme_per_charge: PropTypes.number,
	iec_per_kwh: PropTypes.number,
	energy_vat_percent: PropTypes.number,
	charging_fee_vat_percent: PropTypes.number,
	egme_vat_percent: PropTypes.number,
	iec_vat_percent: PropTypes.number,
})

/**
 * PropTypes for TariffSummary component.
 * @typedef {Object} TariffSummaryProps
 * @property {Object} [tariff] - Display tariff payload from checkoutData.tariff
 * @property {Object|null} [cpoTariff] - CPO tariff as published, from checkoutData.cpo_tariff
 * @property {Object|null} [priceList] - Active CEME price list, from checkoutData.price_list
 */
export const tariffSummaryPropTypes = {
	tariff: displayTariffPropTypes,
	cpoTariff: displayTariffPropTypes,
	priceList: displayPriceListPropTypes,
}

/**
 * PropTypes for the tariff rows inside TariffSummary.
 * @typedef {Object} TariffRulesProps
 * @property {Object} tariff - Display tariff with a rule
 */
export const tariffRulesPropTypes = {
	tariff: displayTariffPropTypes.isRequired,
}

/**
 * PropTypes for ConnectorSelector component.
 * @typedef {Object} ConnectorSelectorProps
 * @property {Array<Object>} connectors - Available connectors list.
 * @property {string} connectors[].id - Connector identifier.
 * @property {string} connectors[].standard - Connector standard (e.g. IEC_62196_T2).
 * @property {string} connectors[].format - Connector format (e.g. SOCKET).
 * @property {string} connectors[].power_type - Power type (e.g. AC_3_PHASE).
 * @property {number} connectors[].max_electric_power - Max power in kW.
 * @property {number} connectors[].max_voltage - Max voltage in V.
 * @property {number} connectors[].max_amperage - Max amperage in A.
 * @property {Object} evseDetails - EVSE details.
 * @property {string} evseDetails.name - EVSE display name or UID.
 * @property {string} [evseDetails.status] - EVSE status.
 * @property {Object} locationInfo - Location details.
 * @property {string} locationInfo.name - Location name.
 * @property {string} locationInfo.address - Location address.
 * @property {string} locationInfo.city - Location city.
 * @property {string} [operatorName] - Operator name.
 * @property {string|null} [selectedConnectorId] - Currently selected connector ID.
 * @property {Function} onSelect - Connector selection callback.
 */
export const connectorSelectorPropTypes = {
	connectors: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			standard: PropTypes.string.isRequired,
			format: PropTypes.string.isRequired,
			power_type: PropTypes.string.isRequired,
			max_electric_power: PropTypes.number.isRequired,
			max_voltage: PropTypes.number.isRequired,
			max_amperage: PropTypes.number.isRequired,
		})
	).isRequired,
	evseDetails: PropTypes.shape({
		name: PropTypes.string.isRequired,
		status: PropTypes.string,
	}).isRequired,
	locationInfo: PropTypes.shape({
		name: PropTypes.string.isRequired,
		address: PropTypes.string.isRequired,
		city: PropTypes.string.isRequired,
	}).isRequired,
	operatorName: PropTypes.string,
	tariff: displayTariffPropTypes,
	cpoTariff: displayTariffPropTypes,
	priceList: displayPriceListPropTypes,
	selectedConnectorId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
	onSelect: PropTypes.func.isRequired,
}

/**
 * PropTypes for CustomerInfoForm component.
 * @typedef {Object} CustomerInfoFormProps
 * @property {string} [initialEmail] - Initial email value.
 * @property {string} [initialName] - Initial name value.
 * @property {string} [initialVat] - Initial VAT/NIF value (optional).
 * @property {Function} onSubmit - Submit handler receiving { email, name, vat }.
 * @property {Function} onBack - Back navigation handler.
 */
export const customerInfoFormPropTypes = {
	initialEmail: PropTypes.string,
	initialName: PropTypes.string,
	initialVat: PropTypes.string,
	onSubmit: PropTypes.func.isRequired,
	onBack: PropTypes.func,
}

/**
 * PropTypes for PaymentError component.
 * @typedef {Object} PaymentErrorProps
 * @property {string} error - Error message to display.
 * @property {string} [errorKey] - Translation key for error message.
 * @property {Function} onRetry - Retry handler.
 */
export const paymentErrorPropTypes = {
	error: PropTypes.string.isRequired,
	errorKey: PropTypes.string,
	onRetry: PropTypes.func.isRequired,
}

/**
 * PropTypes for PaymentSuccess component.
 * @typedef {Object} PaymentSuccessProps
 * @property {Object} sessionData - Session data from confirmation.
 * @property {string} sessionData.session_id - OCPI session ID.
 * @property {string} sessionData.evse_uid - EVSE UID.
 * @property {Object} evseDetails - EVSE and location details.
 * @property {string} evseDetails.name - EVSE display name.
 * @property {Object} evseDetails.location - Location info.
 * @property {string} evseDetails.location.name - Location name.
 * @property {string} evseDetails.location.address - Location address.
 * @property {string} connectorType - Selected connector label.
 */
export const paymentSuccessPropTypes = {
	sessionData: PropTypes.shape({
		session_id: PropTypes.string.isRequired,
		evse_uid: PropTypes.string.isRequired,
	}).isRequired,
	evseDetails: PropTypes.shape({
		name: PropTypes.string.isRequired,
		location: PropTypes.shape({
			name: PropTypes.string.isRequired,
			address: PropTypes.string.isRequired,
		}).isRequired,
	}).isRequired,
	connectorType: PropTypes.string.isRequired,
	qrToken: PropTypes.string,
	paymentRecordId: PropTypes.string,
}

/**
 * PropTypes for StripePaymentForm component.
 * @typedef {Object} StripePaymentFormProps
 * @property {number} amount - Pre-authorization amount.
 * @property {string} currency - Currency code (e.g. EUR).
 * @property {Function} onSuccess - Called with payment intent id on success.
 * @property {Function} onError - Called with error object on failure.
 * @property {Function} onBack - Back navigation handler.
 */
export const stripePaymentFormPropTypes = {
	amount: PropTypes.number.isRequired,
	currency: PropTypes.string,
	onSuccess: PropTypes.func.isRequired,
	onError: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
}

/**
 * PropTypes for MyposHostedCheckout component.
 * @typedef {Object} MyposHostedCheckoutProps
 * @property {number} amount - Pre-authorization amount.
 * @property {string} currency - Currency code (e.g. EUR).
 * @property {Object} paymentPayload - Payload containing redirect URL and fields.
 * @property {string} paymentPayload.redirect_url - myPOS checkout URL.
 * @property {Object} paymentPayload.fields - Form fields for POST redirect.
 * @property {string} [paymentPayload.order_id] - myPOS order ID.
 * @property {string} qrToken - QR token for session storage fallback.
 * @property {string} connectorId - Selected connector ID.
 * @property {Function} onBack - Back navigation handler.
 */
export const myposHostedCheckoutPropTypes = {
	amount: PropTypes.number.isRequired,
	currency: PropTypes.string,
	paymentPayload: PropTypes.shape({
		redirect_url: PropTypes.string.isRequired,
		fields: PropTypes.object.isRequired,
		order_id: PropTypes.string,
	}).isRequired,
	qrToken: PropTypes.string,
	connectorId: PropTypes.string,
	onBack: PropTypes.func.isRequired,
}
